// src/features/auth/api.ts

import { api } from "#/lib/api-client";
import type {
  AuthTokenResponse,
  CurrentUserResponse,
  LoginRequest,
  LoginResponse,
  MfaDisableRequest,
  MfaStatusResponse,
  MfaVerifyRequest,
  PasswordChallengeResponse,
  PasswordResetCompleteRequest,
  PasswordResetRequest,
  ChangePasswordRequest,
  SendEmailChallengeRequest,
  SendEmailChallengeResponse,
  TotpConfirmRequest,
  TotpInitResponse,
} from "./types";

export async function login(payload: LoginRequest) {
  const { data } = await api.post<LoginResponse>("/api/auth/login", payload);
  return data;
}

export async function getCurrentUser() {
  const { data } = await api.get<CurrentUserResponse>("/api/auth/me");
  return data;
}

export async function logout() {
  await api.post("/api/auth/logout");
}

/** Slide the session window — returns a fresh JWT with a reset TTL. */
export async function refreshToken() {
  const { data } = await api.post<AuthTokenResponse>("/api/auth/refresh");
  return data;
}

export async function requestPasswordReset(payload: PasswordResetRequest) {
  const { data } = await api.post<PasswordChallengeResponse>(
    "/api/auth/password-reset/request",
    payload,
  );
  return data;
}

export async function completePasswordReset(payload: PasswordResetCompleteRequest) {
  await api.post("/api/auth/password-reset/complete", payload);
}
export async function changePassword(payload: ChangePasswordRequest) {
  await api.post("/api/auth/change-password", payload);
}

// ── MFA ──────────────────────────────────────────────────────────────────────

/** Complete MFA step after a login challenge; returns full JWT. */
export async function verifyMfa(payload: MfaVerifyRequest) {
  const { data } = await api.post<AuthTokenResponse>("/api/auth/mfa/verify", payload);
  return data;
}

/**
 * Trigger sending an email OTP for an existing login challenge.
 * Call this when the user picks "Email OTP" on the method chooser.
 * The backend generates a new OTP, stores it hashed on the challenge, and emails it.
 */
export async function sendEmailMfaChallenge(payload: SendEmailChallengeRequest) {
  const { data } = await api.post<SendEmailChallengeResponse>(
    "/api/auth/mfa/challenge/email",
    payload,
  );
  return data;
}

/** Check whether MFA is enabled for the current authenticated user. */
export async function getMfaStatus() {
  const { data } = await api.get<MfaStatusResponse>("/api/auth/mfa/status");
  return data;
}

/** Initiate TOTP setup — returns QR code data-URL + plain secret for display. */
export async function initTotpSetup() {
  const { data } = await api.post<TotpInitResponse>("/api/auth/mfa/setup/totp/init");
  return data;
}

/** Confirm TOTP setup by verifying the first code from the authenticator app. */
export async function confirmTotpSetup(payload: TotpConfirmRequest) {
  await api.post("/api/auth/mfa/setup/totp/confirm", payload);
}

/** Enable email OTP for the current user (single call, OTP sent at login time). */
export async function enableEmailMfa() {
  await api.post("/api/auth/mfa/setup/email/enable");
}

/** Disable MFA entirely; requires the user's current password. */
export async function disableMfa(payload: MfaDisableRequest) {
  await api.post("/api/auth/mfa/disable", payload);
}